const TITLE_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with",
  "unit",
  "chapter",
  "week",
  "day",
  "lesson",
  "assignment",
  "part",
  "period",
  "section",
  "block",
  "review",
  "practice",
  "draft",
  "final",
]);

const SECTION_LABEL_STOP_WORDS = new Set([
  "section",
  "period",
  "block",
  "class",
  "group",
  "cohort",
]);

const PLACEHOLDER_TITLE_CANONICAL = new Set([
  "title here",
  "assignment title",
  "assignment name",
  "name here",
  "your title here",
  "add title",
  "insert title",
  "untitled assignment",
  "untitled",
  "new assignment",
]);

const TASK_TYPE_RULES = [
  {
    label: "quizzes and checks",
    keywords: ["quiz", "check", "exit ticket", "cfu", "bellringer", "bell ringer"],
  },
  {
    label: "essays and writing",
    keywords: ["essay", "writing", "response", "dbq", "argument", "paragraph"],
  },
  {
    label: "projects and creations",
    keywords: ["project", "build", "design", "prototype", "portfolio", "create"],
  },
  {
    label: "labs and investigations",
    keywords: ["lab", "investigation", "experiment", "simulation"],
  },
  {
    label: "discussion and presentation work",
    keywords: ["discussion", "seminar", "debate", "presentation", "speech"],
  },
  {
    label: "reading and analysis tasks",
    keywords: ["read", "reading", "analysis", "annotat", "source", "close read"],
  },
];

const normalizeText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeSummarySource = (value) =>
  String(value || "").toLowerCase() === "manual" ? "manual" : "auto";

const toCanonicalTitle = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const isPlaceholderTitle = (value) => {
  const canonical = toCanonicalTitle(value);
  if (!canonical) return true;
  return PLACEHOLDER_TITLE_CANONICAL.has(canonical);
};

const toNaturalList = (items = []) => {
  const values = items.filter(Boolean);
  if (values.length === 0) return "";
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
};

const singularizeToken = (token) => {
  if (token.length <= 3) return token;
  if (token.endsWith("ies") && token.length > 4) {
    return `${token.slice(0, -3)}y`;
  }
  if (token.endsWith("ing") && token.length > 5) {
    return token.slice(0, -3);
  }
  if (token.endsWith("ed") && token.length > 4) {
    return token.slice(0, -2);
  }
  if (token.endsWith("es") && token.length > 4) {
    return token.slice(0, -2);
  }
  if (token.endsWith("sis") && token.length > 3) {
    return token;
  }
  if (token.endsWith("s") && token.length > 4) {
    return token.slice(0, -1);
  }
  return token;
};

const tokenizeForThemes = (value) => {
  const cleaned = normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ");
  return cleaned
    .split(/\s+/)
    .map((token) => singularizeToken(token))
    .filter(
      (token) =>
        token &&
        token.length >= 3 &&
        !TITLE_STOP_WORDS.has(token) &&
        !/^\d+$/.test(token)
    );
};

const rankEntriesByCount = (entries = []) =>
  [...entries].sort((left, right) => {
    if (right[1] !== left[1]) return right[1] - left[1];
    return left[0].localeCompare(right[0]);
  });

const dedupeTitles = (titles = []) => {
  const map = new Map();
  titles.forEach((title) => {
    const normalized = normalizeText(title);
    if (!normalized) return;
    if (isPlaceholderTitle(normalized)) return;
    const key = normalized.toLowerCase();
    if (!map.has(key)) {
      map.set(key, normalized);
    }
  });
  return Array.from(map.values());
};

const findSectionSpecificTitles = (titles = [], sectionName = "", subsection = null) => {
  const titleList = dedupeTitles(titles);
  const sectionTokens = tokenizeForThemes(`${sectionName} ${subsection || ""}`).filter(
    (token) => !SECTION_LABEL_STOP_WORDS.has(token)
  );
  if (sectionTokens.length === 0) return [];
  const matched = titleList.filter((title) => {
    const lower = title.toLowerCase();
    return sectionTokens.some((token) => lower.includes(token));
  });
  return matched;
};

export const analyzeAssignmentTitles = (titles = []) => {
  const uniqueTitles = dedupeTitles(titles);
  if (uniqueTitles.length === 0) {
    return {
      assignmentCount: 0,
      topics: [],
      taskTypes: [],
      sampleTitles: [],
      summary: "No assignment names are available for this class yet.",
    };
  }

  const tokenCounts = new Map();
  const bigramCounts = new Map();
  const taskTypeCounts = new Map();

  uniqueTitles.forEach((title) => {
    const lowerTitle = title.toLowerCase();
    TASK_TYPE_RULES.forEach((rule) => {
      if (rule.keywords.some((keyword) => lowerTitle.includes(keyword))) {
        taskTypeCounts.set(rule.label, (taskTypeCounts.get(rule.label) || 0) + 1);
      }
    });

    const tokens = tokenizeForThemes(title);
    tokens.forEach((token) => {
      tokenCounts.set(token, (tokenCounts.get(token) || 0) + 1);
    });
    for (let index = 0; index < tokens.length - 1; index += 1) {
      const nextIndex = index + 1;
      const bigram = `${tokens[index]} ${tokens[nextIndex]}`;
      bigramCounts.set(bigram, (bigramCounts.get(bigram) || 0) + 1);
    }
  });

  const topics = [];
  const seenTopics = new Set();
  const addTopic = (value) => {
    const normalized = normalizeText(value).toLowerCase();
    if (!normalized || seenTopics.has(normalized)) return;
    seenTopics.add(normalized);
    topics.push(normalized);
  };

  rankEntriesByCount(Array.from(bigramCounts.entries()))
    .filter((entry) => entry[1] >= 2)
    .slice(0, 2)
    .forEach(([topic]) => addTopic(topic));

  rankEntriesByCount(Array.from(tokenCounts.entries()))
    .forEach(([topic]) => addTopic(topic));

  const topTopics = topics.slice(0, 4);
  const taskTypes = rankEntriesByCount(Array.from(taskTypeCounts.entries()))
    .map(([label]) => label)
    .slice(0, 3);
  const sampleTitles = uniqueTitles.slice(0, 5);
  const topicsText =
    topTopics.length > 0 ? toNaturalList(topTopics.slice(0, 3)) : "core course standards";
  const taskText = taskTypes.length > 0 ? toNaturalList(taskTypes.slice(0, 2)) : "";

  return {
    assignmentCount: uniqueTitles.length,
    topics: topTopics,
    taskTypes,
    sampleTitles,
    summarySource: "auto",
    summary: taskText
      ? `Assignment names mostly center on ${topicsText}, with frequent ${taskText}.`
      : `Assignment names mostly center on ${topicsText}.`,
  };
};

export const buildAssignmentNameAnalysis = ({
  classId = null,
  className = "",
  canvasCourseId = null,
  assignmentTitles = [],
  sections = [],
} = {}) => {
  const overall = analyzeAssignmentTitles(assignmentTitles);
  const normalizedSections = Array.isArray(sections) ? sections : [];

  const sectionAnalyses = normalizedSections.map((section) => {
    const sectionName = normalizeText(section?.sectionName || section?.name || "Unassigned");
    const subsection = normalizeText(section?.subsection || "") || null;
    const sectionSpecificTitles = findSectionSpecificTitles(
      assignmentTitles,
      sectionName,
      subsection
    );
    const titlesForSection =
      sectionSpecificTitles.length >= 2 ? sectionSpecificTitles : assignmentTitles;
    const sectionSummary = analyzeAssignmentTitles(titlesForSection);
    return {
      sectionKey: normalizeText(section?.sectionKey || `${sectionName}::${subsection || ""}`),
      sectionName,
      subsection,
      studentCount: Number.isFinite(section?.studentCount)
        ? Number(section.studentCount)
        : 0,
      assignmentCount: sectionSummary.assignmentCount,
      topics: sectionSummary.topics,
      taskTypes: sectionSummary.taskTypes,
      sampleTitles: sectionSummary.sampleTitles,
      summarySource: "auto",
      summary: sectionSummary.summary,
    };
  });

  return {
    version: 1,
    classId: classId !== null && classId !== undefined ? String(classId) : null,
    className: normalizeText(className),
    canvasCourseId:
      canvasCourseId !== null && canvasCourseId !== undefined
        ? String(canvasCourseId)
        : null,
    assignmentCount: overall.assignmentCount,
    topics: overall.topics,
    taskTypes: overall.taskTypes,
    sampleTitles: overall.sampleTitles,
    summarySource: "auto",
    summary: overall.summary,
    sections: sectionAnalyses,
  };
};

const toStringArray = (values) =>
  Array.isArray(values)
    ? values
        .map((value) => normalizeText(value))
        .filter(Boolean)
    : [];

const normalizeSectionEntry = (section) => {
  if (!section || typeof section !== "object") return null;
  const sectionName = normalizeText(section.sectionName || section.name || "Unassigned");
  if (!sectionName) return null;
  const subsection = normalizeText(section.subsection || "") || null;
  return {
    sectionKey: normalizeText(section.sectionKey || `${sectionName}::${subsection || ""}`),
    sectionName,
    subsection,
    studentCount: Number.isFinite(section.studentCount)
      ? Number(section.studentCount)
      : 0,
    assignmentCount: Number.isFinite(section.assignmentCount)
      ? Number(section.assignmentCount)
      : 0,
    topics: toStringArray(section.topics),
    taskTypes: toStringArray(section.taskTypes),
    sampleTitles: toStringArray(section.sampleTitles),
    summarySource: normalizeSummarySource(section.summarySource),
    summary: normalizeText(section.summary),
  };
};

export const normalizeAssignmentNameAnalysis = (value) => {
  if (!value || typeof value !== "object") return null;
  return {
    version: Number.isFinite(Number(value.version)) ? Number(value.version) : 1,
    classId: value.classId !== null && value.classId !== undefined ? String(value.classId) : null,
    className: normalizeText(value.className),
    canvasCourseId:
      value.canvasCourseId !== null && value.canvasCourseId !== undefined
        ? String(value.canvasCourseId)
        : null,
    assignmentCount: Number.isFinite(value.assignmentCount)
      ? Number(value.assignmentCount)
      : 0,
    topics: toStringArray(value.topics),
    taskTypes: toStringArray(value.taskTypes),
    sampleTitles: toStringArray(value.sampleTitles),
    summarySource: normalizeSummarySource(value.summarySource),
    summary: normalizeText(value.summary),
    sections: Array.isArray(value.sections)
      ? value.sections.map((section) => normalizeSectionEntry(section)).filter(Boolean)
      : [],
  };
};

export const buildPromptCandidatesFromAssignmentAnalysis = (
  analysis,
  { classLabel = "this class", limit = 6 } = {}
) => {
  const normalized = normalizeAssignmentNameAnalysis(analysis);
  if (!normalized) return [];
  const prompts = [];
  const seen = new Set();
  const safeClassLabel = normalizeText(classLabel) || "this class";
  const maxPrompts = Number.isFinite(limit) ? Math.max(1, Number(limit)) : 6;

  const addPrompt = (value) => {
    const prompt = normalizeText(value);
    if (!prompt) return;
    const key = prompt.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    prompts.push(prompt);
  };

  if (normalized.topics.length > 0 && normalized.taskTypes.length > 0) {
    addPrompt(
      `Students will apply ${normalized.topics[0]} through ${normalized.taskTypes[0]} in ${safeClassLabel}.`
    );
  }

  if (normalized.topics.length >= 2) {
    addPrompt(
      `Students will compare ${normalized.topics[0]} and ${normalized.topics[1]} using evidence from class materials.`
    );
  } else if (normalized.topics.length === 1) {
    addPrompt(
      `Students will explain the key ideas of ${normalized.topics[0]} and justify their conclusions with evidence.`
    );
  }

  normalized.sampleTitles.slice(0, 2).forEach((title) => {
    addPrompt(
      `Students will build on learning from "${title}" to complete an aligned performance task.`
    );
  });

  normalized.sections.slice(0, 2).forEach((section) => {
    const sectionTopic = section.topics[0] || normalized.topics[0] || "";
    if (!sectionTopic) return;
    const sectionLabel = section.subsection
      ? `${section.sectionName} (${section.subsection})`
      : section.sectionName;
    addPrompt(
      `Students in ${sectionLabel} will deepen understanding of ${sectionTopic} through guided practice and discussion.`
    );
  });

  return prompts.slice(0, maxPrompts);
};
